import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { TreeNodeData } from "@douyinfe/semi-ui/lib/es/tree";
import { downloadFileApi } from "@services/file/file.api";
import {
  AnnotationModel,
  AlignFileCacheModel,
  AlignResultCacheModel,
  ViewerPayloadModel,
  AnnotationPayloadModel,
  GroupModel,
  SequenceContentModel,
  MMSeqs2Model,
  AlignCacheModel,
  SequenceFileModel,
  FileItemModel,
  SequenceFileUpdateModel,
  SequenceViewerModel,
  AnnotationContentModel,
  VisibleSequenceMetaModel,
  GroupActiveModel,
  EngineTypePayloadModel,
  ColorSchemeTypePayloadModel,
  UIContentModel
} from "@services/sequence-viewer/model/sequence-viewer.model";
import { SequenceSourceType } from "@services/sequence-viewer/enum/sequence-viewer.enum";
import {
  createSequenceViewerApi,
  createSequenceFileApi,
  updateSequenceFileApi,
  getSequenceViewerDetailApi
} from "@services/sequence-viewer/sequence-viewer.api";
import { RootState } from "../store";

interface ViewerScopedPayload<T> {
  viewerId: string;
  value: T;
}

interface ViewerVisibilityPayload {
  viewerId: string;
  ids: string[];
  hidden: boolean;
}

export interface SequenceViewerInstanceState {
  databaseAnnotations: AnnotationModel[];
  treeDataList: TreeNodeData[];
  rootNode: TreeNodeData;
  annotations: AnnotationModel[];
  alignFileMap: AlignFileCacheModel;
  a3mAlignCacheMap: AlignResultCacheModel;
  fileItemMap: Record<string, FileItemModel>;
  isExplorerLoading: boolean;
  annotationGroupList: string[];
  activeRightTab: string;
  isSaving: boolean;
  sequenceAnnotGroupMap: Record<string, string[]>;
  sequenceGroupAnnotGroupMap: Record<string, string[]>;
  groupActiveData: Record<string, GroupActiveModel>;
}

export interface SequenceViewerState {
  sequenceViewerList: SequenceViewerModel[];
  byId: Record<string, SequenceViewerInstanceState>;
}

function createInitialViewerState(): SequenceViewerInstanceState {
  return {
    databaseAnnotations: [],
    treeDataList: [
      {
        label: "Ungrouped",
        value: "ungrouped",
        key: "ungrouped",
        level: 0
      }
    ],
    rootNode: {
      label: "All Sequences",
      value: "all",
      key: "all",
      level: 0
    },
    annotations: [],
    alignFileMap: {},
    a3mAlignCacheMap: {},
    fileItemMap: {},
    isExplorerLoading: false,
    annotationGroupList: [],
    activeRightTab: "annotations",
    isSaving: false,
    sequenceAnnotGroupMap: {},
    sequenceGroupAnnotGroupMap: {},
    groupActiveData: {}
  };
}

const defaultSequenceViewerState = createInitialViewerState();

const initialState: SequenceViewerState = {
  sequenceViewerList: [],
  byId: {}
};

function getSequenceViewerState(
  state: SequenceViewerState,
  viewerId: string
): SequenceViewerInstanceState {
  if (!state.byId[viewerId]) {
    state.byId[viewerId] = createInitialViewerState();
  }

  return state.byId[viewerId];
}

function buildFileItemMap(
  fileItems: FileItemModel[] = []
): Record<string, FileItemModel> {
  return fileItems.reduce((ac, item) => {
    if (item.content_identifier === "sequences") {
      ac.sequences = item;
    } else if (item.content_identifier === "groups") {
      ac.groups = item;
    } else if (item.content_identifier === "annotations") {
      ac.annotations = item;
    } else if (item.content_identifier === "align") {
      ac.align = item;
    } else if (item.content_identifier === "user_interface") {
      ac.ui = item;
    }

    return ac;
  }, {} as Record<string, FileItemModel>);
}

export function selectSequenceViewerById(
  state: RootState,
  viewerId: string
): SequenceViewerInstanceState {
  return state.sequenceViewer.byId[viewerId] ?? defaultSequenceViewerState;
}

export function selectSequenceViewerList(state: RootState) {
  return state.sequenceViewer.sequenceViewerList;
}

async function persistentData(
  type: string,
  viewerId: string,
  state: SequenceViewerInstanceState,
  viewerName?: string
) {
  const {
    treeDataList,
    rootNode,
    annotations,
    alignFileMap,
    a3mAlignCacheMap,
    fileItemMap,
    annotationGroupList,
    groupActiveData
  } = state;

  const allTreeList = [rootNode, ...treeDataList];

  const annotationsList = annotations.map((item) => {
    const o: AnnotationPayloadModel = {
      id: item.id,
      label: item.label,
      color: item.color,
      ranges: item.ranges,
      style: item.style,
      position: item.position,
      hidden: item.hidden,
      group: item.group
    };

    return o;
  });

  const groupContent = allTreeList.map((item) => {
    const o: GroupModel = {
      id: item.value as string,
      name: item.label as string
    };

    if (item.a3mAlignId) {
      o.a3mAlignId = item.a3mAlignId as string;
    }

    if (item.alignId) {
      o.alignId = item.alignId as string;
    }

    return o;
  });

  const sequenceContent = allTreeList.reduce((ac, group) => {
    const seqs = group.children?.map((item) => {
      const o: SequenceContentModel = {
        id: item.value as string,
        name: item.label as string,
        sequence: item.sequence as string,
        groupId: `${group.value!}`,
        hash: item.hash as string
      };

      if (item.source) {
        o.source = item.source as SequenceSourceType;
      }

      if (item.mmSeqs2) {
        o.mmSeqs2 = item.mmSeqs2 as MMSeqs2Model;
      }

      if (item.sourceIdentifier) {
        o.sourceIdentifier = item.sourceIdentifier as string;
      }

      return o;
    });

    if (seqs) {
      return [...ac, ...seqs];
    }

    return ac;
  }, [] as SequenceContentModel[]);

  const alignContent: AlignCacheModel = {
    alignFile: alignFileMap,
    a3mAlignResult: a3mAlignCacheMap
  };

  const annotationContent: AnnotationContentModel = {
    annotations: annotationsList,
    annotationGroups: annotationGroupList
  };

  const uiContent: UIContentModel = {
    groupActiveData
  };

  if (type === "save") {
    const annotPayload: SequenceFileModel = {
      sequence_viewer_id: viewerId,
      content_identifier: "annotations",
      content: JSON.stringify(annotationContent),
      filename: "annotations.json"
    };

    const groupPayload: SequenceFileModel = {
      sequence_viewer_id: viewerId,
      content_identifier: "groups",
      content: JSON.stringify(groupContent),
      filename: "groups.json"
    };

    const sequencesPayload: SequenceFileModel = {
      sequence_viewer_id: viewerId,
      content_identifier: "sequences",
      content: JSON.stringify(sequenceContent),
      filename: "sequences.json"
    };

    const alignFilesPayload: SequenceFileModel = {
      sequence_viewer_id: viewerId,
      content_identifier: "align",
      content: JSON.stringify(alignContent),
      filename: "aligns.json"
    };

    const uiPayload: SequenceFileModel = {
      sequence_viewer_id: viewerId,
      content_identifier: "user_interface",
      content: JSON.stringify(uiContent),
      filename: "user_interface.json"
    };

    const annotPromise = createSequenceFileApi(annotPayload);
    const groupPromise = createSequenceFileApi(groupPayload);
    const sequencesPromise = createSequenceFileApi(sequencesPayload);
    const alignPromise = createSequenceFileApi(alignFilesPayload);
    const uiPromise = createSequenceFileApi(uiPayload);

    try {
      const ids = await Promise.all([
        annotPromise,
        groupPromise,
        sequencesPromise,
        alignPromise,
        uiPromise
      ]);

      const params: SequenceFileModel[] = [
        annotPayload,
        groupPayload,
        sequencesPayload,
        alignFilesPayload,
        uiPayload
      ];

      const fileItems = ids.map((id, idx) => {
        const item = params[idx];
        const obj: FileItemModel = {
          file_id: id,
          content_identifier: item.content_identifier
        };

        return obj;
      });

      const viewer: SequenceViewerModel = {
        id: viewerId,
        name: viewerName ?? "",
        file_item_list: fileItems
      };

      return viewer;
    } catch (e) {
      console.error(e);
    }
  } else {
    if (!fileItemMap.annotations || !fileItemMap.groups) {
      return;
    }

    if (!fileItemMap.sequences || !fileItemMap.align) {
      return;
    }

    const annotPayload: SequenceFileUpdateModel = {
      sequence_viewer_id: viewerId,
      content_identifier: fileItemMap.annotations.content_identifier,
      content: JSON.stringify(annotationContent)
    };

    const groupPayload: SequenceFileUpdateModel = {
      sequence_viewer_id: viewerId,
      content_identifier: fileItemMap.groups.content_identifier,
      content: JSON.stringify(groupContent)
    };

    const sequencesPayload: SequenceFileUpdateModel = {
      sequence_viewer_id: viewerId,
      content_identifier: fileItemMap.sequences.content_identifier,
      content: JSON.stringify(sequenceContent)
    };

    const alignFilesPayload: SequenceFileUpdateModel = {
      sequence_viewer_id: viewerId,
      content_identifier: fileItemMap.align.content_identifier,
      content: JSON.stringify(alignContent)
    };

    let uiParams: SequenceFileUpdateModel | SequenceFileModel;

    const annotPromise = updateSequenceFileApi(annotPayload);
    const groupPromise = updateSequenceFileApi(groupPayload);
    const sequencesPromise = updateSequenceFileApi(sequencesPayload);
    const alignPromise = updateSequenceFileApi(alignFilesPayload);
    let uiPromise: Promise<string>;

    const { file_item_list: fileItemList } = await getSequenceViewerDetailApi(
      viewerId
    );

    const fileMap = fileItemList.reduce((ac, item) => {
      ac[item.content_identifier] = item.file_id;
      return ac;
    }, {} as Record<string, string>);

    if (fileMap.user_interface) {
      const uiPayload: SequenceFileUpdateModel = {
        sequence_viewer_id: viewerId,
        content_identifier: "user_interface",
        content: JSON.stringify(uiContent)
      };

      uiParams = uiPayload;

      uiPromise = updateSequenceFileApi(uiPayload);
    } else {
      const uiPayload: SequenceFileModel = {
        sequence_viewer_id: viewerId,
        content_identifier: "user_interface",
        content: JSON.stringify(uiContent),
        filename: "user_interface.json"
      };

      uiParams = uiPayload;

      uiPromise = createSequenceFileApi(uiPayload);
    }

    try {
      const ids = await Promise.all([
        annotPromise,
        groupPromise,
        sequencesPromise,
        alignPromise,
        uiPromise
      ]);

      const params: SequenceFileModel | SequenceFileUpdateModel[] = [
        annotPayload,
        groupPayload,
        sequencesPayload,
        alignFilesPayload,
        uiParams
      ];

      const fileItems = ids.map((id, idx) => {
        const item = params[idx];
        const obj: FileItemModel = {
          file_id: id,
          content_identifier: item.content_identifier
        };

        return obj;
      });

      const viewer: SequenceViewerModel = {
        id: viewerId,
        name: viewerName ?? "",
        file_item_list: fileItems
      };

      return viewer;
    } catch (e) {
      console.error(e);
    }
  }
}

export const saveViewerData = createAsyncThunk<
  SequenceViewerModel | undefined,
  ViewerPayloadModel & { viewerId: string },
  { state: RootState }
>("sequenceViewer/saveViewerData", async (p, { getState }) => {
  const state = getState();
  const viewerState = selectSequenceViewerById(state, p.viewerId);
  const { data } = await createSequenceViewerApi(p);
  const viewer = await persistentData("save", data.id, viewerState, p.name);
  return viewer;
});

export const updateViewerData = createAsyncThunk<
  SequenceViewerModel | undefined,
  string,
  { state: RootState }
>("sequenceViewer/updateViewerData", async (viewerId, { getState }) => {
  const state = getState();
  const viewerState = selectSequenceViewerById(state, viewerId);
  const viewer = await persistentData("update", viewerId, viewerState);

  return viewer;
});

export const initializeData = createAsyncThunk<
  | [
      TreeNodeData[],
      AnnotationModel[],
      AlignCacheModel,
      Record<string, FileItemModel>,
      string[],
      UIContentModel | null
    ]
  | undefined,
  string,
  { state: RootState }
>("sequenceViewer/initializeData", async (viewerId) => {
  try {
    const data = await getSequenceViewerDetailApi(viewerId);

    const fileItems: FileItemModel[] = data.file_item_list;
    const fileMap = buildFileItemMap(fileItems);

    const annotPromise = downloadFileApi(
      {
        fids: [fileMap.annotations.file_id],
        access_source: "sequence_viewer",
        access_source_id: viewerId
      },
      "",
      "text"
    );
    const groupPromise = downloadFileApi(
      {
        fids: [fileMap.groups.file_id],
        access_source: "sequence_viewer",
        access_source_id: viewerId
      },
      "",
      "text"
    );

    const sequencesPromise = downloadFileApi(
      {
        fids: [fileMap.sequences.file_id],
        access_source: "sequence_viewer",
        access_source_id: viewerId
      },
      "",
      "text"
    );

    const alignPromise = downloadFileApi(
      {
        fids: [fileMap.align.file_id],
        access_source: "sequence_viewer",
        access_source_id: viewerId
      },
      "",
      "text"
    );

    let uiPromise: Promise<string | null> = Promise.resolve(null);

    if (fileMap.ui) {
      uiPromise = downloadFileApi(
        {
          fids: [fileMap.ui.file_id],
          access_source: "sequence_viewer",
          access_source_id: viewerId
        },
        "",
        "text"
      ) as Promise<string>;
    }

    const [annotData, groupData, sequencesData, alignData, uiData] =
      await Promise.all([
        annotPromise,
        groupPromise,
        sequencesPromise,
        alignPromise,
        uiPromise
      ]);

    const annotJson = JSON.parse(annotData as string) as
      | AnnotationPayloadModel[]
      | AnnotationContentModel;

    const groupJson = JSON.parse(groupData as string) as GroupModel[];

    const sequencesJson = JSON.parse(
      sequencesData as string
    ) as SequenceContentModel[];

    const alignJson = JSON.parse(alignData as string) as AlignCacheModel;

    let uiJson: UIContentModel | null = null;

    if (uiData) {
      uiJson = JSON.parse(uiData) as UIContentModel;
    }

    let newAnnotList: AnnotationModel[] = [];
    let annotGroups: string[] = [];

    if (Array.isArray(annotJson)) {
      newAnnotList = annotJson.map((item) => {
        const o: AnnotationModel = {
          ...item,
          type: "manual",
          description: ""
        };

        return o;
      });
    } else {
      newAnnotList = annotJson.annotations.map((item) => {
        const o: AnnotationModel = {
          ...item,
          type: "manual",
          description: ""
        };

        return o;
      });

      annotGroups = annotJson.annotationGroups;
    }

    const sequenceMap = sequencesJson.reduce((ac, item) => {
      if (!ac[item.groupId]) {
        ac[item.groupId] = [item];
      } else {
        ac[item.groupId].push(item);
      }
      return ac;
    }, {} as Record<string, SequenceContentModel[]>);

    const newGroups = groupJson.map((group) => {
      const o: TreeNodeData = {
        label: group.name,
        value: group.id,
        key: group.id,
        level: 0
      };

      if (group.a3mAlignId) {
        o.a3mAlignId = group.a3mAlignId;
      }
      if (group.alignId) {
        o.alignId = group.alignId;
      }

      if (sequenceMap[group.id]) {
        o.children = sequenceMap[group.id].map((item) => {
          const obj: TreeNodeData = {
            label: item.name,
            value: item.id,
            key: item.id,
            sequence: item.sequence,
            level: 1,
            hash: item.hash
          };

          if (item.source) {
            obj.source = item.source;
          }

          if (item.mmSeqs2) {
            obj.mmSeqs2 = item.mmSeqs2;
          }

          if (item.sourceIdentifier) {
            obj.sourceIdentifier = item.sourceIdentifier;
          }

          return obj;
        });
      }

      return o;
    });

    return [
      newGroups,
      newAnnotList,
      alignJson,
      fileMap,
      annotGroups,
      uiJson
    ] as const;
  } catch (e) {
    console.error(e);
  }
});

export const SequenceViewerSlice = createSlice({
  name: "sequenceViewer",
  initialState,

  reducers: {
    setDatabaseAnnotations: (
      state,
      action: PayloadAction<ViewerScopedPayload<AnnotationModel[]>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.databaseAnnotations = action.payload.value;
    },
    toggleDbAnnotsVisibility: (
      state,
      action: PayloadAction<ViewerVisibilityPayload>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      const { ids, hidden } = action.payload;

      const newAnnotations = viewerState.databaseAnnotations.map((a) => {
        const o: AnnotationModel = { ...a };

        if (ids.includes(a.id)) {
          o.hidden = hidden;
        }

        return o;
      });

      viewerState.databaseAnnotations = newAnnotations;
    },
    setTreeDataList: (
      state,
      action: PayloadAction<ViewerScopedPayload<TreeNodeData[]>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.treeDataList = action.payload.value;
    },
    setRootNode: (
      state,
      action: PayloadAction<ViewerScopedPayload<TreeNodeData>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.rootNode = action.payload.value;
    },
    setAnnotations: (
      state,
      action: PayloadAction<ViewerScopedPayload<AnnotationModel[]>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.annotations = action.payload.value;
    },
    batchAddAnnotation: (
      state,
      action: PayloadAction<ViewerScopedPayload<AnnotationModel[]>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.annotations = [
        ...viewerState.annotations,
        ...action.payload.value
      ];
    },
    batchToggleAnnotation: (
      state,
      action: PayloadAction<
        ViewerScopedPayload<{ ids: string[]; hidden?: boolean }>
      >
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.annotations = viewerState.annotations.map((a) => {
        const o: AnnotationModel = { ...a };
        const { ids, hidden } = action.payload.value;
        if (ids.includes(a.id)) {
          o.hidden = !hidden;
        }
        return o;
      });
    },
    batchDeleteAnnotation: (
      state,
      action: PayloadAction<ViewerScopedPayload<string[]>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.annotations = viewerState.annotations.filter((a) => {
        return !action.payload.value.includes(a.id);
      });
    },
    setAlignFileMap: (
      state,
      action: PayloadAction<ViewerScopedPayload<AlignFileCacheModel>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.alignFileMap = action.payload.value;
    },
    setA3mAlignCacheMap: (
      state,
      action: PayloadAction<ViewerScopedPayload<AlignResultCacheModel>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.a3mAlignCacheMap = action.payload.value;
    },
    setSequenceViewerList: (
      state,
      action: PayloadAction<SequenceViewerModel[]>
    ) => {
      state.sequenceViewerList = action.payload;
    },
    setAnnotationGroupList: (
      state,
      action: PayloadAction<ViewerScopedPayload<string[]>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.annotationGroupList = action.payload.value;
    },
    setActiveRightTab: (
      state,
      action: PayloadAction<ViewerScopedPayload<string>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.activeRightTab = action.payload.value;
    },
    resetSequenceEditorData: () => {
      return initialState;
    },
    removeViewerState: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
    },
    setSequenceAnnotGroupMap: (
      state,
      action: PayloadAction<ViewerScopedPayload<Record<string, string[]>>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.sequenceAnnotGroupMap = action.payload.value;
    },
    setSequenceGroupAnnotGroupMap: (
      state,
      action: PayloadAction<ViewerScopedPayload<Record<string, string[]>>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      viewerState.sequenceGroupAnnotGroupMap = action.payload.value;
    },
    setEngineType: (
      state,
      action: PayloadAction<ViewerScopedPayload<EngineTypePayloadModel>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      const { groupId, engineType } = action.payload.value;
      viewerState.groupActiveData[groupId] = {
        ...viewerState.groupActiveData[groupId],
        engineType
      };
    },
    setColorScheme: (
      state,
      action: PayloadAction<ViewerScopedPayload<ColorSchemeTypePayloadModel>>
    ) => {
      const viewerState = getSequenceViewerState(
        state,
        action.payload.viewerId
      );
      const { groupId, colorScheme } = action.payload.value;
      viewerState.groupActiveData[groupId] = {
        ...viewerState.groupActiveData[groupId],
        colorScheme
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeData.pending, (state, action) => {
        const viewerState = getSequenceViewerState(state, action.meta.arg);
        viewerState.isExplorerLoading = true;
      })
      .addCase(initializeData.fulfilled, (state, action) => {
        const viewerState = getSequenceViewerState(state, action.meta.arg);

        if (action.payload) {
          const [
            newGroups,
            newAnnotList,
            alignJson,
            fileMap,
            annotGroups,
            uiData
          ] = action.payload;
          const [root, ...rest] = newGroups;
          viewerState.rootNode = root;
          viewerState.treeDataList = rest;
          viewerState.annotations = newAnnotList;
          viewerState.alignFileMap = alignJson.alignFile;
          viewerState.a3mAlignCacheMap = alignJson.a3mAlignResult;
          viewerState.fileItemMap = fileMap;
          viewerState.annotationGroupList = annotGroups;
          if (uiData) {
            viewerState.groupActiveData = uiData.groupActiveData;
          }
        }

        viewerState.isExplorerLoading = false;
      })
      .addCase(initializeData.rejected, (state, action) => {
        const viewerState = getSequenceViewerState(state, action.meta.arg);
        viewerState.isExplorerLoading = false;
      });

    builder
      .addCase(saveViewerData.pending, (state, action) => {
        const viewerState = getSequenceViewerState(
          state,
          action.meta.arg.viewerId
        );
        viewerState.isSaving = true;
      })
      .addCase(saveViewerData.fulfilled, (state, action) => {
        const sourceViewerState = getSequenceViewerState(
          state,
          action.meta.arg.viewerId
        );

        if (action.payload) {
          state.sequenceViewerList.unshift(action.payload);

          if (action.payload.id !== action.meta.arg.viewerId) {
            state.byId[action.payload.id] = {
              ...sourceViewerState,
              fileItemMap: buildFileItemMap(action.payload.file_item_list)
            };
          } else {
            sourceViewerState.fileItemMap = buildFileItemMap(
              action.payload.file_item_list
            );
          }
        }

        sourceViewerState.isSaving = false;
        const targetViewerId = action.payload?.id ?? action.meta.arg.viewerId;
        const targetViewerState = getSequenceViewerState(state, targetViewerId);
        targetViewerState.isSaving = false;
      })
      .addCase(saveViewerData.rejected, (state, action) => {
        const viewerState = getSequenceViewerState(
          state,
          action.meta.arg.viewerId
        );
        viewerState.isSaving = false;
      });

    builder
      .addCase(updateViewerData.pending, (state, action) => {
        const viewerState = getSequenceViewerState(state, action.meta.arg);
        viewerState.isSaving = true;
      })
      .addCase(updateViewerData.fulfilled, (state, action) => {
        const payload = action.payload;
        const viewerState = getSequenceViewerState(state, action.meta.arg);
        viewerState.isSaving = false;

        if (payload) {
          const fileItemList = payload.file_item_list;
          state.sequenceViewerList = state.sequenceViewerList.map((item) => {
            const o = {
              ...item
            };

            if (o.id === payload.id) {
              o.file_item_list = fileItemList;
            }

            return o;
          });
        }
      })
      .addCase(updateViewerData.rejected, (state, action) => {
        const viewerState = getSequenceViewerState(state, action.meta.arg);
        viewerState.isSaving = false;
      });
  }
});

export const {
  setDatabaseAnnotations,
  setTreeDataList,
  setRootNode,
  setAnnotations,
  setAlignFileMap,
  setA3mAlignCacheMap,
  setSequenceViewerList,
  setAnnotationGroupList,
  setActiveRightTab,
  batchAddAnnotation,
  toggleDbAnnotsVisibility,
  resetSequenceEditorData,
  removeViewerState,
  batchToggleAnnotation,
  batchDeleteAnnotation,
  setSequenceAnnotGroupMap,
  setSequenceGroupAnnotGroupMap,
  setEngineType,
  setColorScheme
} = SequenceViewerSlice.actions;

export default SequenceViewerSlice.reducer;
